import { queryOptions } from "@tanstack/react-query";

/** One depot (upload portal) as the Dépôt de fichiers list reads it. */
export interface PortalRow {
  id: string;
  recipientEmail: string;
  description: string | null;
  recipientInstructions: string | null;
  token: string;
  status: string;
  expiresAt: string;
  createdAt: string | null;
  fileCount: number;
  usedBytes: number;
  quotaBytes: number;
  /** Same threshold as the detail page and the public portal. */
  isFull: boolean;
  /** The full depot this one replaces; the list folds it under this row. */
  continuesPortalId: string | null;
  lastUploadAt: string | null;
}

/** The user's upload portals (Dépôt de fichiers). */
export const uploadPortalsQueryOptions = queryOptions({
  queryKey: ["upload-portals"],
  queryFn: async (): Promise<PortalRow[]> => {
    const res = await fetch("/api/upload-portals", { cache: "no-store" });

    if (!res.ok) throw new Error("Impossible de charger vos dépôts.");
    // SAFETY: /api/upload-portals is our own route returning { portals } per the PortalRow contract.
    const data = (await res.json()) as { portals: PortalRow[] };

    return data.portals;
  },
});

export interface DepotFile {
  id: string;
  fileId: string;
  uploadedByEmail: string;
  uploadedAt: string;
  notifiedAt: string | null;
  name: string;
  size: number;
  mimeType: string;
  scanStatus: string;
  scanResult: { viruses: string[]; scannedAt: string } | null;
  scannedAt: string | null;
  loi25Flag: boolean | null;
  encryptedAtRest: boolean | null;
}

export interface DepotConsent {
  id: string;
  userEmail: string;
  containsPiiDeclared: boolean;
  consentGiven: boolean;
  consentTimestamp: string;
  consentTextVersion: string;
  hasPdfAttestation: boolean;
  fileCount: number;
}

export interface DepotDetail {
  portal: {
    id: string;
    recipientEmail: string;
    description: string | null;
    recipientInstructions: string | null;
    token: string;
    status: string;
    expiresAt: string;
    createdAt: string | null;
    quotaFullNotifiedAt: string | null;
  };
  uploadedFiles: DepotFile[];
  storage: { usedBytes: number; quotaBytes: number; remainingBytes: number; isFull: boolean };
  consents: DepotConsent[];
  successorPortal: {
    id: string;
    token: string;
    description: string | null;
    expiresAt: string;
    createdAt: string | null;
  } | null;
  predecessorPortal: { id: string; description: string | null; recipientEmail: string } | null;
}

/** Scans still running: the depot refreshes until every file has a verdict. */
const SCAN_POLL_MS = 5000;

export function isScanInFlight(file: Pick<DepotFile, "scanStatus">) {
  return file.scanStatus === "pending" || file.scanStatus === "scanning";
}

/** A deleted (or someone else's) depot: terminal, so the page offers no retry. */
export class DepotNotFoundError extends Error {
  constructor() {
    super("Ce dépôt n'existe plus.");
  }
}

/** One depot with its files, storage, Loi 25 registry and successor. */
export const uploadPortalQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ["upload-portals", id],
    queryFn: async (): Promise<DepotDetail> => {
      const res = await fetch(`/api/upload-portals/${id}`, { cache: "no-store" });

      if (res.status === 404 || res.status === 403) throw new DepotNotFoundError();

      if (!res.ok) throw new Error("Impossible de charger le dépôt.");

      // SAFETY: /api/upload-portals/:id returns the DepotDetail shape per its route contract.
      return (await res.json()) as DepotDetail;
    },
    // A deleted depot is terminal, not transient: don't retry.
    retry: false,
    // A fresh upload shows « Analyse en cours » until ClamAV answers; poll so
    // the download unlocks without a manual refresh.
    refetchInterval: (query) =>
      query.state.data?.uploadedFiles.some(isScanInFlight) ? SCAN_POLL_MS : false,
  });
